import angular from 'angular';
import _ from 'lodash-es';
import { KAlgo } from '~/components/code-editor/kalgo';
import Tabulator from 'tabulator-tables';
import { UserModel } from '~/scripts/services/model/user';
import APIV5 from '~/models/api';
import Defaults from '~/scripts/constants/defaults';
import QueryVariable from '~/scripts/services/model/query-variable';
import {
	createFileThumb,
	createWorkflowCell,
	rowNumberColumnDefinition,
	rowSelectionColumnDefinition,
} from '~/components/ktable/columnFactory';
import Utils from '~/scripts/services/utils';
import { customColumns } from '~/models/query';
import { nanoid } from 'nanoid';
import { makeNodeName } from '~/components/ktable/utils';
import { Alert } from '~/scripts/services/modal/modal';
import QueryColumn from '~/scripts/services/model/query-column';
import QueryRule from '~/scripts/services/model/query-rule';
import QueryWhereRule from '~/scripts/services/model/query-where-rule';
import templateModalForm from '~/components/kform/modal/modal-form.html';
import ModalFormController from '~/components/kform/modal/modal-form.controller';
import { algoResolve } from './modal/modal-query-select/code-editor/algo';
import { removeQuotes } from './modal/modal-query-select/code-editor/columnWidget';
import templateModalQuerySelect from './modal/modal-query-select.html';
// eslint-disable-next-line import/no-cycle
import ModalQuerySelectController from './modal/modal-query-select.controller';
import tableFooter from './components/footer.html';
import tableHeader from './components/header.html';
import tableCell from './components/cell.html';
import templateModalQueryAdmin from './modal/modal-query-admin.html';
import templateModalQueryPath from './modal/modal-query-path.html';
import templateModalQueryMfSubQuery from './modal/modal-query-mf-sub-query.html';
import templateEmptyResults from './components/empty-results.html';
import ModalQueryAdminController from './modal/modal-query-admin.controller';
import ModalQueryPathController from './modal/modal-query-path.controller';
import ModalQueryMfSubQueryController from './modal/modal-query-mf-sub-query.controller';
// eslint-disable-next-line unicorn/prevent-abbreviations
import ModalQueryDownloadDocsZipController from './modal/modal-query-download-docs-zip.controller';
// eslint-disable-next-line unicorn/prevent-abbreviations
import templateModalQueryDownloadDocsZip from './modal/modal-query-download-docs-zip.html';

/* globals Knitiv Dashboard */

// These imports are just used for describe some properties in class
// -----------------------------------------------------------------
// eslint-disable-next-line import/no-cycle,import/order
// eslint-disable-next-line import/no-cycle,import/order

/**
	* @typedef {{ expression: string; columnsUsed: string[]; name: string }} Process
	*/

/**
	* @typedef {Record<string, Process>} Processes
	*/

const DUMMY = '__dummy__';

/** @type {any} */
export const operators = {
	none:             {
		type:      'none',
		nb_inputs: 0,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [
				'boolean',
				'datetime',
				'number',
				'string',
			],
			field: [
				'attribut',
				'droit',
				'kid',
				'repre',
			],
		},
	},
	equal_kid:        {
		type:      'equal_kid',
		nb_inputs: 1,
		multiple:  false,
		mf:        true,
		apply_to:  {
			type: [
				'string',
			],
			field: [
				'attribut',
				'isa',
				'kid',
			],
		},
	},
	not_equal_kid:    {
		type:      'not_equal_kid',
		nb_inputs: 1,
		multiple:  false,
		mf:        true,
		apply_to:  {
			type: [
				'string',
			],
			field: [
				'attribut',
				'isa',
				'kid',
			],
		},
	},
	equal_value:      {
		type:      'equal_value',
		nb_inputs: 1,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [
				'boolean',
				'datetime',
				'number',
				'string',
			],
			field: [
				'attribut',
				'datecreation',
				'dated',
				'datef',
				'droit',
				'file',
				'ref_propri',
				'repre',
			],
		},
	},
	not_equal_value:  {
		type:      'not_equal_value',
		nb_inputs: 1,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [
				'boolean',
				'datetime',
				'number',
				'string',
			],
			field: [
				'attribut',
				'datecreation',
				'dated',
				'datef',
				'droit',
				'file',
				'ref_propri',
				'repre',
			],
		},
	},
	is_active:        {
		type:      'is_active',
		nb_inputs: 0,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [
				'boolean',
				'datetime',
				'number',
				'string',
			],
			field: [
				'isa',
				'kid',
			],
		},
	},
	is_inactive:      {
		type:      'is_inactive',
		nb_inputs: 0,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [
				'boolean',
				'datetime',
				'number',
				'string',
			],
			field: [
				'file',
				'isa',
				'kid',
				'repre',
			],
		},
	},
	in:               {
		type:      'in',
		nb_inputs: 1,
		multiple:  true,
		mf:        true,
		apply_to:  {
			type: [
				'string',
			],
			field: [
				'kid',
			],
		},
	},
	not_in:           {
		type:      'not_in',
		nb_inputs: 1,
		multiple:  true,
		mf:        true,
		apply_to:  {
			type: [
				'string',
			],
			field: [
				'kid',
			],
		},
	},
	less:             {
		type:      'less',
		nb_inputs: 1,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [
				'datetime',
				'number',
			],
			field: [
				'datecreation',
				'dated',
				'datef',
				'kid',
				'repre',
			],
		},
	},
	less_or_equal:    {
		type:      'less_or_equal',
		nb_inputs: 1,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [
				'datetime',
				'number',
			],
			field: [
				'datecreation',
				'dated',
				'datef',
				'kid',
				'repre',
			],
		},
	},
	greater:          {
		type:      'greater',
		nb_inputs: 1,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [
				'datetime',
				'number',
			],
			field: [
				'datecreation',
				'dated',
				'datef',
				'kid',
				'repre',
			],
		},
	},
	greater_or_equal: {
		type:      'greater_or_equal',
		nb_inputs: 1,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [
				'datetime',
				'number',
			],
			field: [
				'datecreation',
				'dated',
				'datef',
				'kid',
				'repre',
			],
		},
	},
	between:          {
		type:      'between',
		nb_inputs: 2,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [
				'datetime',
				'number',
			],
			field: [
				'datecreation',
				'dated',
				'datef',
				'kid',
				'repre',
			],
		},
	},
	not_between:      {
		type:      'not_between',
		nb_inputs: 2,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [
				'datetime',
				'number',
			],
			field: [
				'datecreation',
				'dated',
				'datef',
				'kid',
				'repre',
			],
		},
	},
	begins_with:      {
		type:      'begins_with',
		nb_inputs: 1,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [
				'string',
			],
			field: [
				'attribut',
				'ref_propri',
				'repre',
			],
		},
	},
	not_begins_with:  {
		type:      'not_begins_with',
		nb_inputs: 1,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [
				'string',
			],
			field: [
				'attribut',
				'ref_propri',
				'repre',
			],
		},
	},
	contains:         {
		type:      'contains',
		nb_inputs: 1,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [
				'string',
			],
			field: [
				'attribut',
				'ref_propri',
				'repre',
			],
		},
	},
	not_contains:     {
		type:      'not_contains',
		nb_inputs: 1,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [
				'string',
			],
			field: [
				'attribut',
				'ref_propri',
				'repre',
			],
		},
	},
	ends_with:        {
		type:      'ends_with',
		nb_inputs: 1,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [
				'string',
			],
			field: [
				'attribut',
				'ref_propri',
				'repre',
			],
		},
	},
	not_ends_with:    {
		type:      'not_ends_with',
		nb_inputs: 1,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [
				'string',
			],
			field: [
				'attribut',
				'ref_propri',
				'repre',
			],
		},
	},
	is_empty:         {
		type:      'is_empty',
		nb_inputs: 0,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [
				'string',
			],
			field: [
				'attribut',
				'ref_propri',
				'repre',
			],
		},
	},
	is_not_empty:     {
		type:      'is_not_empty',
		nb_inputs: 0,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [
				'string',
			],
			field: [
				'attribut',
				'repre',
			],
		},
	},
	is_null:          {
		type:      'is_null',
		nb_inputs: 0,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [],
			field: [
				'kid',
				'repre',
			],
		},
	},
	is_not_null:      {
		type:      'is_not_null',
		nb_inputs: 0,
		multiple:  false,
		mf:        false,
		apply_to:  {
			type: [],
			field: [
				'kid',
				'repre',
			],
		},
	},
};

/** @type {Record<string,any>} */
export const operatorsByType = {};

/** @type {Tabulator.ColumnDefinition} */
const rownumColumn = {
	// eslint-disable-next-line object-shorthand
	formatter: function (cell) {
		// @ts-ignore
		// eslint-disable-next-line no-underscore-dangle
		return this.table.rowManager.activeRows.indexOf(cell.getRow()._getSelf()) + 1
			// @ts-ignore
			+ ((this.table.getPage() - 1) * this.table.getPageSize());
	},
	frozen:     true,
	headerSort: false,
	hozAlign:   'right',
	resizable:  false,
	title:      '',
	width:      100,
};

for (const [k, op] of Object.entries(operators)) {
	for (const type of op.apply_to.type) {
		if (!(type in operatorsByType)) {
			operatorsByType[type] = [];
		}
		operatorsByType[type].push({
			id:        k,
			name:      `query.operators.${k}`,
			nb_inputs: op.nb_inputs,
			multiple:  op.multiple,
			mf:        op.mf,
			fields:    op.apply_to.field,
		});
	}
}

/** @type {any} */
export const types = {
	'K_NODE;BOOL':      'boolean',
	'K_NODE;CHAR':      'string',
	'K_NODE;CRYPT':     'string',
	'K_NODE;DATE':      'datetime',
	'K_NODE;DATETIME':  'datetime',
	'K_NODE;DOUBLE':    'number',
	'K_NODE;DOUBLE8':   'number',
	'K_NODE;HASH':      'string',
	'K_NODE;HTML':      'string',
	'K_NODE;INTEGER':   'number',
	'K_NODE;JSON':      'string',
	'K_NODE;NUMBER':    'number',
	'K_NODE;PATH':      'string',
	'K_NODE;REFERENCE': 'string',
	'K_NODE;STRING':    'string',
	'K_NODE;TEXT':      'string',
	'K_NODE;TIME':      'datetime',
};

export default class QueryEditorController {
	/** @type {import('angular').ICompileService} */
	$compile;

	/** @type {import('angular').IRootElementService} */
	$element;

	/** @type {import('angular').IFilterService} */
	$filter;

	/** @type {import('angular').ILocationService} */
	$location;

	/** @type {import('angular').IRootScopeService} */
	$rootScope;

	/** @type {any} */
	$scope;

	/** @type {import('angular').ITimeoutService} */
	$timeout;

	/** @type {import('angular').translate.ITranslateService} */
	$translate;

	/** @type {import('angular').ui.bootstrap.IModalService} */
	$uibModal;

	/** @type {import('angular').IWindowService} */
	$window;

	/** @type {UserModel} */
	User;

	/** @type {?import('../query-tab').default} */
	tab;

	/** @type {?Tabulator} */
	table;

	/* @ngInject */
	/**
	 * @param {import('angular').ICompileService} $compile
	 * @param {any} $copyToClipboard
	 * @param {import('angular').IRootElementService} $element
	 * @param {import('angular').IFilterService} $filter
	 * @param {import('angular').auto.IInjectorService} $injector
	 * @param {import('angular').ILocationService} $location
	 * @param {Knitiv.RootScope} $rootScope
	 * @param {any} $scope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {import('angular').ui.bootstrap.IModalService} $uibModal
	 * @param {import('angular').IWindowService} $window
	 * @param {any} FileDownloadService
	 * @param {import('~/scripts/services/form.service').default} FormService
	 * @param {any} NodeService
	 * @param {any} Notification
	 * @param {UserModel} User
	 */
	constructor(
		$compile,
		$copyToClipboard,
		$element,
		$filter,
		$injector,
		$location,
		$rootScope,
		$scope,
		$timeout,
		$translate,
		$uibModal,
		$window,
		FileDownloadService,
		FormService,
		NodeService,
		Notification,
		User,
	) {
		this.$compile = $compile;
		this.$element = $element;
		this.$filter = $filter;
		this.$location = $location;
		this.$rootScope = $rootScope;
		this.$scope = $scope;
		this.$timeout = $timeout;
		this.$translate = $translate;
		this.$uibModal = $uibModal;
		this.$window = $window;
		this.$injector = $injector;
		this.FileDownloadService = FileDownloadService;
		this.FormService = FormService;
		this.NodeService = NodeService;
		this.Notification = Notification;
		this.User = User;
		this.$copyToClipboard = $copyToClipboard;

		/** @type {?import('~/pages/query/query.controller').default} */
		this.ctrl = null;

		/** @type {Dashboard.DragDropScope} */
		this.dragDropScope = {
			/** @type {?any} */
			// @ts-ignore
			item: null,
			onDrop: item => this.onDrop(item),
			addColumn: index => this.addColumn(index),
		};

		this.index = 0;
		this.isAdmin = undefined;
		this.initializing = true;
		this.loadingWidgets = false;
		this.ngModelCtrl = undefined;
		this.operators = operators;

		/** @type {Tabulator.Options} */
		this.options = {
			ajaxConfig:             {
				method:  'POST',
				headers: {
					'Content-type': 'application/json; charset=utf-8',
				},
			},
			ajaxLoaderLoading:      `
<i class="fa fa-circle-o-notch fa-spin spinner fa-2x"></i>
			`,
			ajaxLoaderError:        `
<i class="fa fa-circle-o-notch fa-spin spinner fa-2x"></i>
			`,
			ajaxRequesting:         () => !this.initializing,
			ajaxRequestFunc:        (url, config, parameters) => this.exec(parameters),
			ajaxSorting:            false,
			ajaxURL:                `${APIV5.instance.backEndpoint}/api/v${Defaults.api.version}/QueryExec`,
			columns:                [rownumColumn],
			columnMoved:            (column, columns) => this.columnMoved(column, columns),
			columnResized:          () => this.$timeout(() => this.queryEditorForm?.$setDirty()),
			headerSort:             false,
			height:                 '100%',
			layout:                 'fitDataTable',
			layoutColumnsOnNewData: false,
			minHeight:              230,
			movableColumns:         true,
			pageLoaded:             () => {
				if (this.tab) {
					this.tab.packet_size = this.table?.getPageSize() || 0;
				}
			},
			pagination:             'remote',
			paginationButtonCount:  5,
			paginationSizeSelector: [50, 100, 200, 500],
			persistence:            {
				columns: ['width'],
			},
			// @ts-ignore
			persistenceMode:        'query',
			placeholder:            '',
		};

		/** @type {?import('angular').IFormController} */
		this.queryEditorForm = null;

		this.queryForm = undefined;

		this.selectOperations = ['detail', 'count', 'sum', 'min', 'avg', 'max'];

		/** @type {?import('../query-tab').default} */
		this.tab = null;

		/** @type {?Tabulator} */
		this.table = null;

		this.isDragging = false;

		/** @type {import('~/scripts/services/model/node')[]} */
		this.widgets = [];

		Tabulator.prototype.extendModule('persistence', 'readers', {
			query: (/** @type {any} */ id, /** @type {string} */ type) => {
				if (type === 'columns') {
					return this.tab?.query.select.map(select => ({ field: select.field, width: select.width }));
				}
				return [];
			},
		});

		Tabulator.prototype.extendModule('persistence', 'writers', {
			query: (/** @type {any} */ id, /** @type {string} */ type, /** @type {any} */ data) => {
				// id - tables persistence id
				// type - type of data being persisted ('sort', 'filter', 'group', 'page' or 'columns')
				// data - array or object of data
				if (type === 'columns' && this.tab?.edit) {
					for (const column of data) {
						if (column.field) {
							const sel = this.tab.query.select.find(select => select.field === column.field);
							if (sel) {
								sel.width = column.width;
							}
						}
					}
				}
			},
		});
	}

	/**
	 * @param {number} index
	 * @param {QueryColumn} select
	 * @param {any} [position]
	 * @returns {Promise<any>}
	 */
	async insertColumn(index, select, position = index) {
		const width = 180; // Match dndPlaceholder width

		// vvv Génere un field pour la colonne
		const field = `column-${this.index}`;
		select.field = field;

		if (!select.id) {
			select.id = nanoid();
		}

		select.virtual = select.type === 'virtual';

		if (customColumns.includes(select.type) && select.type !== 'query') {
			select.kid = this.tab?.query.from[0].node;
			select.path = this.tab?.query.from[0].path;
			select.node = 'REPRE';
		}

		/** @type {Tabulator.ColumnDefinition} */
		let columnOptions;

		switch (select.type) {
		case 'rownum': {
			columnOptions = rowNumberColumnDefinition({
				field,
				title: this.$translate.instant(select.name),
				// @ts-ignore
				titleFormatter: cell => this.titleFormatter(field, cell, select),
				resizable: true,
			});
			break;
		}
		case 'fileThumb': {
			columnOptions = createFileThumb(() => {}, this.$injector, (/** @type {Tabulator.CellComponent} */ cell) => {
				/** @type {any} */
				const data = cell.getData();
				return { root: data[field][0] };
			}, {
				field,
				title: this.$translate.instant(select.name),
				// @ts-ignore
				titleFormatter: cell => this.titleFormatter(field, cell, select),
				resizable: true,
			});
			break;
		}
		case 'rowSelection': {
			columnOptions = rowSelectionColumnDefinition({
				field,
				title: this.$translate.instant(select.name),
				// @ts-ignore
				titleFormatter: cell => this.titleFormatter(field, cell, select),
				resizable: true,
			});
			break;
		}
		case 'workflow': {
			columnOptions = createWorkflowCell({
				field,
				title: this.$translate.instant(select.name),
				// @ts-ignore
				titleFormatter: cell => this.titleFormatter(field, cell, select),
				resizable: true,
			},
			() => {},
			this.$translate,
			this.$scope.$new(true),
			this.$compile,
			(/** @type {Tabulator.CellComponent} */ cell) => {
				const value = cell.getValue();
				if (value && value[0]) {
					return value[0].wf;
				}
				return { count: 0 };
			});
			break;
		}
		default: {
			columnOptions = {
				field,
				// @ts-ignore
				formatter:      (cell, parameters, onRendered) => this.cellFormatter(cell, parameters, onRendered),
				title:          select.name,
				// @ts-ignore
				titleFormatter: cell => this.titleFormatter(field, cell, select),
			};
		}
		}

		columnOptions.width = select.width ?? width;

		await this.table?.addColumn(columnOptions, false, position);

		return select;
	}

	/**
	 * @param {number} index
	 * @returns {Promise<any>}
	 */
	async addColumn(index) {
		// Delete dummy column in case there is one
		if (this.table?.getColumn(DUMMY)) {
			await this.table.deleteColumn(DUMMY);
		}

		const { item } = this.dragDropScope;
		if (item.formatter) {
			item.type = item.formatter;
		}

		if (item.parent && !item.node) {
			// @ts-ignore
			item.node = item.parent;
		}

		if (item.type === 'query') {
			/** @type {any} */
			try {
				const subQueryItem = await this.$uibModal.open({
					ariaLabelledBy:  'modal-query-mf-sub-query-title',
					ariaDescribedBy: 'modal-query-mf-sub-query-body',
					template:        templateModalQueryMfSubQuery,
					controller:      ModalQueryMfSubQueryController,
					controllerAs:    '$ctrl',
					size:            'md',
					resolve:         {
						item: () => item,
					},
				}).result;
				item.node = subQueryItem.kid;
				item.name = subQueryItem.name;
				item.path.length = 0;
			}
			catch (error) {
				if (error !== 'cancel') {
					console.error(error);
				}
				return false;
			}
		}

		if (item) {
			const select = this.tab?.addSelect(item);
			if (select) {
				const columns = this.table?.getColumns();
				if (Array.isArray(columns)) {
					const position = columns[index - 1];
					const newSelect = await this.insertColumn(index, select, position);
					this.tab?.query.select.splice(index - 1, 0, newSelect);
					this.table?.clearData();
					this.$timeout(async () => {
						this.queryEditorForm?.$setDirty();
						this.resizeColumn(newSelect.field);
						this.resizeEditor();
					});

					this.index++;

					if (item.type === 'virtual') {
						await this.openSelectModal(newSelect, 'expression');
					}
				}
			}
		}
		return item;
	}

	/**
	 * @param {any} variable
	 * @returns {QueryVariable}
	 */
	addVariable(variable) {
		const model = Object.assign(new QueryVariable(), variable);

		// replace existing non-instanciated variable
		_.remove(
			// @ts-ignore
			this.tab?.query.variable,
			(/** @type {any} */ v) => !(v instanceof QueryVariable) && v.id === variable.id && v.isa === variable.isa,
		);
		this.createVariable(model);

		return model;
	}

	// eslint-disable-next-line class-methods-use-this
	/**
	 * @param {Tabulator.CellComponent} cell
	 * @param {any} parameters
	 * @param {Function} onRendered
	 * @returns {Element}
	 */
	cellFormatter(cell, parameters, onRendered) {
		const scope = this.$scope.$new();
		// @ts-ignore
		scope.cells = cell.getValue();
		// @ts-ignore
		scope.$ctrl = this;
		// @ts-ignore
		scope.$tableElement = this.$element.find('.tabulator-table');

		angular.element(cell.getElement()).addClass('bg-gray-400');
		const element = this.$compile(tableCell)(scope)[0];
		onRendered(() => {
			if (!this.$rootScope.$$phase) {
				scope.$digest();
			}
		});
		return element;
	}

	/**
	 * @param {Tabulator.ColumnComponent} column
	 * @param {any[]} columns
	 */
	columnMoved(column, columns) {
		const mapIndex = columns.map(col => col.getField());
		this.$timeout(() => this.queryEditorForm?.$setDirty());

		this.tab?.query.select.sort((a, b) => {
			if (mapIndex.indexOf(a.field) < mapIndex.indexOf(b.field)) {
				return -1;
			}
			if (mapIndex.indexOf(a.field) > mapIndex.indexOf(b.field)) {
				return 1;
			}
			return 0;
		});
	}

	/**
	 * @param {string} text
	 */
	copy(text) {
		this.$copyToClipboard(
			text,
			true,
			(/** @type {any} */ v) => this.$filter('sprintf')(
				this.$translate.instant('common.x-copied-to-clipboard'),
				`<samp>${v}</samp>`,
			),
		);
	}

	/**
		* Create a variable
		*
		* @param {QueryVariable} variable
		*/
	createVariable(variable) {
		if (!variable.id) {
			variable.id = this.User.getRef();
		}
		this.tab?.query.variable.push(variable);
		this.queryEditorForm?.$setDirty();
	}

	/**
	 * @param {QueryVariable} variable
	 */
	deleteVariable(variable) {
		// 1. Check if this variable is used in rules
		if (this.tab?.query.findRule(
			(/** @type {QueryRule | QueryWhereRule} */ r) => r.variable === variable.id) !== undefined
		) {
			this.Notification.error({
				title:     this.$translate.instant('error.title'),
				message:   this.$translate.instant('query.errors.delete_variable_used'),
			});
		}
		else if (this.tab?.query.variable) {
			_.remove(this.tab?.query.variable, v => v.id === variable.id && v.isa === variable.isa);

			this.queryEditorForm?.$setDirty();
		}
	}

	edit() {
		if (this.tab) {
			this.tab.edit = !this.tab.edit;
			this.$location.search({ edit: this.tab.edit || null, kid: this.tab.node.kid });
		}
		this.resizeEditor();
		this.$timeout(() => this.table?.redraw());
	}

	/**
	 * @param {?(import('@knitiv/api-client-javascript').KNode | string)} kid
	 * @param {boolean} [create]
	 */
	async editWidget(kid, create = false) {
		// @ts-ignore
		const result = await this.FormService.load(create ? 'K_NODE;WIDGET' : kid, { rg: true, create });
		const { form, submission } = result;
		if (create && !kid) {
			// eslint-disable-next-line no-param-reassign
			kid = `K_NODE;${this.User.getRef()}`;
			submission.data.K_DEFINED_BY_KSYSQUERY_0 = [
				{
					kid:  this.tab?.node.kid,
					name: this.tab?.node.getName(),
				},
			];
		}
		const componentQuery = form.getComponent('K_DEFINED_BY_KSYSQUERY_0');
		if (componentQuery) {
			componentQuery.disabled = true;
		}

		form.submission = await this.$uibModal.open({
			ariaLabelledBy:  'modal-form-title',
			ariaDescribedBy: 'modal-form-body',
			template:        templateModalForm,
			controller:      ModalFormController,
			controllerAs:    '$ctrl',
			resolve:         {
				form:       () => form,
				options:    () => ({
					title:  create ? this.$translate.instant('query.new-widget') : form.getName(),
					footer: true,
					form:   {
						displayExpandButton: false,
					},
				}),
			},
		}).result;

		if (typeof kid === 'string') {
			form.modified = true;
			const resultSave = await this.FormService.save({ [kid]: form }, new Set().add(kid));

			if (resultSave) {
				if (create) {
					this.Notification.success({
						title:   this.$translate.instant('object.successfully-created-title'),
						message: this.$translate.instant('object.successfully-created-message'),
					});
				}
				else {
					this.Notification.success({
						title:   this.$translate.instant('object.successfully-updated-title'),
						message: this.$translate.instant('object.successfully-updated-message'),
					});
				}
				await this.loadWidgets(true);
			}
		}
	}

	/**
	 * @param {{ size:number, page: number }} parameters
	 * @returns {Promise<{data: *[], last_page: number}>}
	 */
	async exec(parameters) {
		const size = parameters?.size;
		const page = parameters?.page;

		const packetIndex = (page - 1) * size + 1;
		const data = [];

		if (this.tab && this.tab.query.from.length > 0 && this.tab.query.select.length > 0) {
			this.tab.loading = true;

			const queryExport = this.tab.query.export();
			const queryExecCall = queryExport.version >= '3.0.0' ? 'queryExecV3' : 'queryExec';

			try {
				const result = await APIV5.instance[queryExecCall]({
					query:        queryExport,
					variable:     Object.keys(this.tab.variable).length > 0 ? this.tab.variable : undefined,
					packet_index: packetIndex,
					packet_size:  size,
					// @ts-ignore
					_kid:         this.tab.create ? undefined : this.tab.node.kid,
				}, { id: 'query-editor.exec' });

				this.tab.loading = false;

				this.tab.message = this.$filter('sprintf')(
					this.$translate.instant('query.status-message'),
					result.result.rows.length,
					result.result.total,
					result.result.duration * 1000,
				);

				this.$timeout(() => {
					this.$scope.$apply();
				});

				for (const row of result.result.rows) {
					/** @type {any} */
					const newRow = {};
					for (const [c, cell] of row.entries()) {
						newRow[this.tab.query.select[c].field] = cell;
					}
					data.push(newRow);
				}

				return {
					last_page: Math.ceil(result.result.total / size),
					data,
				};
			}
			catch (/** @type {any} */ error) {
				this.tab.loading = false;
				this.tab.message = this.$translate.instant('error.title');
				this.Notification.error({
					title:   this.$translate.instant('error.title'),
					message: error.toString(),
				});
			}
		}

		return {
			last_page: 0,
			data,
		};
	}

	async exportDocs() {
		/** @type {{ compressing: boolean, fileid: string | undefined }} */
		const downloadResult = {
			compressing: true,
			fileid:      undefined,
		};
		const modalInstance = await this.$uibModal.open({
			ariaLabelledBy:  'modal-query-download-docs-zip-title',
			ariaDescribedBy: 'modal-query-download-docs-zip-body',
			template:        templateModalQueryDownloadDocsZip,
			controller:      ModalQueryDownloadDocsZipController,
			controllerAs:    '$ctrl',
			resolve:         {
				downloadResult,
			},
		});

		await modalInstance.opened;
		const result = await APIV5.instance.queryExecExportDocZip({
			// @ts-ignore
			query:  this.tab?.query.export(),
			export: 'doc_zip',
		}, { id: 'query-editor.export-docs' });

		this.$timeout(() => {
			downloadResult.compressing = false;
			downloadResult.fileid = result.fileid || undefined;
		});

		const modalResult = await modalInstance.result;
		if (modalResult === 'download') {
			const fileDownloadResult = await this.FileDownloadService.download([{ kid: downloadResult.fileid }]);
			if (fileDownloadResult) {
				Utils.download(fileDownloadResult.data, fileDownloadResult.data.type, fileDownloadResult.name);
			}
		}
	}

	/**
		* Export the query
		*/
	async exportToExcel() {
		const result = await APIV5.instance.queryExecExportXLSX({
			// @ts-ignore
			query:  this.tab?.query.export(),
			export: 'xlsx',
		}, { id: 'query-editor.export-to-excel' });

		const fileDownloadResult = await this.FileDownloadService.download([{ kid: result.fileid }]);
		if (fileDownloadResult) {
			Utils.download(fileDownloadResult.data, fileDownloadResult.data.type, fileDownloadResult.name);
		}
	}

	/**
	 *
	 * @param {Processes} graph
	 * @returns {any[] | undefined}
	 */
	findCycle(graph) {
		const visited = new Set();
		let result;

		/**
		 * @param {string} node
		 * @param {Set<any>} path
		 * @returns {any}
		 */
		function dfs(node, path) {
			if (path.has(node)) {
				result = [...path, node];
				result.splice(0, result.indexOf(node));
				return true;
			}
			if (visited.has(node)) {
				return undefined;
			}
			path.add(node);
			visited.add(node);
			if ((graph?.[node]?.columnsUsed || []).some(child => dfs(child, path))) {
				return path;
			}
			// Backtrack
			path.delete(node);
			return undefined;
		}

		for (const node in graph) {
			if (!visited.has(node) && dfs(node, new Set())) {
				return result;
			}
		}

		return undefined;
	}

	/**
	 * @param {string} variable
	 * @returns {QueryVariable | undefined}
	 */
	findVariable(variable) {
		return this.tab?.query.variable.find(v => v.id === variable);
	}

	/**
	 * @param {string} expression
	 * @returns {Promise<any>}
	 */
	async getColumnsUsed(expression) {
		const { result } = await APIV5.instance.algoGetInfo({
			module: 'query',
		}, { id: 'query-editor.get-columns-used' });

		const resolvedInfos = algoResolve(result);

		const kalgo = KAlgo(resolvedInfos);

		return new Promise(resolve => {
			const tree = kalgo.language.parser.parse(expression);

			/** @type {string[]} */
			const columns = [];

			const cursor = tree.cursor();
			do {
				const text = expression.slice(cursor.from, cursor.to);
				if (
					cursor.node.name === 'FnKeyword'
					&& text === 'COLREF'
				) {
					const realNode = cursor.node;
					const parameter1Node = realNode.nextSibling; // Param
					const rawColumn = parameter1Node?.firstChild?.firstChild; // String
					const columnName = removeQuotes(expression.slice(rawColumn?.from, rawColumn?.to));
					columns.push(columnName);
				}
			}
			while (cursor.next());

			// eslint-disable-next-line no-promise-executor-return
			return resolve(columns);
		});
	}

	async initColumns() {
		if (this.tab?.query.select.length === 0) {
			this.table?.addColumn({
				title:          this.$translate.instant('query.drop-your-column'),
				field:          DUMMY,
				headerSort:     false,
				maxWidth:       false,
				// @ts-ignore
				titleFormatter: cell => this.titleFormatter(DUMMY, cell),
				width:          '100%',
			});
		}
		else {
			for (const select of this.tab?.query.select || []) {
				await this.insertColumn(this.index, select);
				this.index++;
			}
		}
		this.$timeout(() => this.resizeAllColumns());
	}

	isDeletable() {
		return this.tab?.node.checkDroit(Defaults.DROIT.DELETE);
	}

	isEditable() {
		return this.tab?.node.checkDroit(Defaults.DROIT.MODIFY) || this.tab?.node.create;
	}

	async launch() {
		const verifyQuery = await this.verifyQuery();

		if (verifyQuery) {
			this.$timeout(() => {
				this.table?.setPage(1);
			});
		}
	}

	async loadWidgets(force = false) {
		if (this.widgets.length === 0 || force) {
			this.loadingWidgets = true;
			this.widgets.length = 0;
			const result = await this.NodeService.list(
				'K_NODE;WIDGET',
				{
					link: {
						relation_kid: 'K_NODE;COMPOSED_OF',
						class_kid:    'K_NODE;KSYSQUERY',
						linker_kid:   '0',
						objnum2:      this.tab?.node.kid,
					},
				},
			);
			this.loadingWidgets = false;
			this.$timeout(() => {
				this.widgets.push(...result);
			});
		}
	}

	onDragEnd() {
		this.isDragging = false;
	}

	onDragStart() {
		this.isDragging = true;
	}

	/**
	 * @param {any} item
	 * @returns {boolean}
	 */
	onDrop(item) {
		this.dragDropScope.item = item;
		return true;
	}

	openAdmin() {
		this.$uibModal.open({
			ariaLabelledBy:  'modal-query-admin-title',
			ariaDescribedBy: 'modal-query-admin-body',
			template:        templateModalQueryAdmin,
			controller:      ModalQueryAdminController,
			controllerAs:    '$ctrl',
			size:            'sm',
			resolve:         {
				query: () => this.tab?.query,
			},
		});
	}

	/**
	 * @param {any[]} path
	 */
	openPath(path) {
		this.$uibModal.open({
			ariaLabelledBy:  'modal-query-path-title',
			ariaDescribedBy: 'modal-query-path-body',
			template:        templateModalQueryPath,
			controller:      ModalQueryPathController,
			controllerAs:    '$ctrl',
			size:            'sm',
			resolve:         {
				path: () => path,
			},
		});
	}

	/**
	 * @param {import('@knitiv/api-client-javascript').QueryColumn} select
	 * @param {'details' | 'path' | 'filters' | 'expression'} tab
	 */
	async openSelectModal(select, tab = 'details') {
		try {
			const newSelect = await this.$uibModal.open({
				ariaLabelledBy:  'modal-query-select-title',
				ariaDescribedBy: 'modal-query-select-body',
				template:        templateModalQuerySelect,
				controller:      ModalQuerySelectController,
				// animation:       false,
				windowClass:  'modal-query-select',
				controllerAs: '$ctrl',
				size:         'lg',
				resolve:      {
					disabled: () => !this.tab?.edit,
					select:   () => _.cloneDeep(select),
					query:    () => this.tab?.query,
					editor:   () => this,
					tab:      () => (this.tab?.edit ? tab : 'path'),
				},
			}).result;

			if (newSelect) {
				this.queryEditorForm?.$setDirty();
				this.$timeout(() => {
					_.merge(select, newSelect);
					// @ts-ignore
					const cell = this.table?.getColumn(select.field);
					if (cell) {
						// @ts-ignore
						this.setColumnStyle(cell, select);
					}
				});
			}
		}
		catch (error) {
			if (error !== undefined && error !== 'cancel') {
				console.error(error);
			}
		}
	}

	/**
	 * @param {Tabulator.ColumnLookup} field
	 * @param {QueryColumn} select
	 * @returns {import('angular').IPromise<any>}
	 */
	removeColumn(field, select) {
		return this.$timeout(async () => {
			await this.table?.deleteColumn(field);
			const index = this.tab?.query.select.indexOf(select);
			if (index !== undefined && index >= 0) {
				this.tab?.query.select.splice(index, 1);
				this.$timeout(() => this.queryEditorForm?.$setDirty());
			}

			if (this.tab?.query.select.length === 0) {
				this.table?.clearData();
				await this.initColumns();
			}
		});
	}

	async reset() {
		this.tab?.query.reset();
		// @ts-ignore
		this.table.setColumns([rownumColumn]);
		await this.initColumns();
	}

	resizeAllColumns() {
		for (const select of this.tab?.query.select || []) {
			this.resizeColumn(select.field);
		}
	}

	/**
	 * @param {Tabulator.ColumnLookup} field
	 */
	resizeColumn(field) {
		const column = this.table?.getColumn(field);
		const select = this.tab?.query.select.find(select2 => select2.field === field);

		if (column) {
			if (select?.width) {
				column.setWidth(select.width);
			}
			else {
				const labelWidth = this.$element.find(`.tabulator-col[tabulator-field="${field}"] .lib`)?.get(0)?.scrollWidth;

				if (labelWidth && column.getWidth() < labelWidth + 40) {
					column.setWidth(labelWidth + 40);
				}
			}
		}
	}

	resizeEditor() {
		if (this.tab?.edit && this.$element) {
			this.$element.find('.tabulator-col').css('height', '45px');
			const $queryContent = this.$element?.find('.query-content');
			if ($queryContent) {
				const height = $queryContent.height();
				if (height !== undefined) {
					this.$element.find('.query-editor').css('flex-basis', `${height}px`);
				}
			}
		}
		else {
			this.$element.find('.tabulator-col').css('height', '27px');
			this.$element.find('.query-editor').css('flex-basis', 0);
		}
		this.resizeAllColumns();
		this.resizePanelVariables();
		this.table?.redraw(true);
	}

	resizePanelVariables() {
		const $panelVariables = this.$element.find('.panel.variables');
		if ($panelVariables.length > 0) {
			const width = $panelVariables.width();
			if (width !== undefined && width <= 300) {
				$panelVariables.addClass('compact');
			}
			else {
				$panelVariables.removeClass('compact');
			}
		}
	}

	async save() {
		const queryOk = await this.verifyQuery();

		if (queryOk) {
			await this.tab?.save();
			this.$timeout(() => {
				this.Notification.success({
					title: this.$translate.instant('query.save-success'),
					message: this.$translate.instant('query.save-success-description'),
				});
				this.$location.search('create', null);
				this.queryEditorForm?.$setPristine();
			});
		}
	}

	/**
	 * @param {Tabulator.CellComponent | Tabulator.ColumnComponent} cell
	 * @param {?QueryColumn} select
	 */
	setColumnStyle(cell, select) {
		const colElement = angular
			.element(cell.getElement())
			.closest('.tabulator-col');

		if (
			select?.path
			&& select.path.length > 0
			&& (select.path[0].kid === 'PATH_REL' || select.inherits_extended === 0)
		) {
			colElement.addClass('children');
		}
		else {
			colElement.removeClass('children');
		}

		if (select?.type === 'virtual') {
			colElement.addClass('virtual');
		}
		else {
			colElement.removeClass('virtual');
		}
	}

	/**
		* @param {string} field
		* @param {Tabulator.CellComponent} cell
		* @param {any | null} select
		* @returns {Element | undefined} HTML element
		*/
	titleFormatter(field, cell, select = null) {
		// TODO: The timeouts here cause a delay
		// before the cell is rendered

		const scope = this.$scope.$new();

		// @ts-ignore
		scope.$ctrl = {
			editor: this,
			field,
			select,
		};

		const options = {
			threshold: 0.9,
		};

		const observer = new IntersectionObserver(entries => {
			for (const entry of entries) {
				if (entry.intersectionRatio > 0) {
					this.$timeout(() => {
						const htmlElement = this.$compile(tableHeader)(scope).get(0);
						if (htmlElement) {
							// noinspection JSCheckFunctionSignatures
							angular.element(cell.getElement()).html(htmlElement);
						}
					});
				}
			}
		}, options);

		observer.observe(cell.getElement());

		this.$timeout(() => {
			this.setColumnStyle(cell, select);
		});

		return this.$compile(tableHeader)(scope).get(0);
	}

	async verifyQuery() {
		// Verify loops

		/** @type {Processes} */
		const processes = {};
		for (const select of this.tab?.query.select || []) {
			let columns = [];
			if (select.kdata?.expression) {
				columns = await this.getColumnsUsed(select.kdata.expression);
			}

			const expression = select?.kdata?.expression ?? '';

			processes[select.id] = {
				expression,
				columnsUsed: columns,
				name: makeNodeName(select),
			};
		}

		const cycle = this.findCycle(processes);

		if (cycle) {
			const loop = cycle.map(col => processes[col].name);

			const alert = new Alert(this.$injector);

			await alert.open({
				title: 'Boucle détectée',
				body: `Une boucle a été détectée dans votre requête:<br><br>${loop.join(' 🠒 ')}`,
				buttons: [
					{
						label: 'OK',
						action: 'dismiss',
						type: 'info',
					},
				],
			});

			return false;
		}

		// Verify that all columns exist

		const values = Object.values(processes);
		const keys = Object.keys(processes);

		let notFound = '';
		for (const v of values) {
			for (const col of v.columnsUsed) {
				// If the column used in not in the list of available columns
				// Consider the column doesn't exist
				if (!keys.includes(col)) {
					notFound = col;

					const alert = new Alert(this.$injector);

					await alert.open({
						title: 'Colonne inconnue',
						body: `La colonne "${notFound}" n'existe pas dans l'expression de la colonne "${v.name}".`,
						buttons: [
							{
								label: 'OK',
								action: 'dismiss',
								type: 'info',
							},
						],
					});

					break;
				}
			}
		}

		return notFound === '';
	}

	$onInit() {
		this.isAdmin = this.User.getProfil().admin;
		this.options.paginationSize = this.tab?.packet_size;
		this.options.footerElement = this.$compile(tableFooter)(this.$scope)[0];
		this.options.persistenceID = `query-${this.tab?.node.kid?.split(';')[1]}`;
		// @ts-ignore
		this.options.placeholder = this.$compile(templateEmptyResults)(this.$scope)[0];

		this.$scope.$on('angular-resizable.resizing', () => this.resizePanelVariables());
		angular.element(this.$window).bind('resize', () => this.resizePanelVariables());

		this.$timeout(async () => {
			const tabulatorHeaders = angular.element('.tabulator-headers');
			tabulatorHeaders.attr({
				'dnd-list': '',
				'dnd-horizontal-list': 'true',
				'dnd-external-sources': 'true',
				'dnd-drop': '$ctrl.onDrop(item)',
				'dnd-inserted': '$ctrl.addColumn(index)',
			});

			const placeholder = angular.element('<div/>');
			placeholder.addClass('dndPlaceholder');
			tabulatorHeaders.append(placeholder);

			const scope = this.$scope.$new();
			// @ts-ignore
			scope.$ctrl = this.dragDropScope;
			this.$compile(tabulatorHeaders)(scope);

			await this.initColumns();

			this.$timeout(() => {
				this.resizeEditor();
			});

			this.initializing = false;
			this.queryEditorForm = this.$scope.queryForm;
			if (this.tab) {
				this.tab.editorForm = this.queryEditorForm;
				this.tab.editorController = this;
			}

			this.$scope.$apply();
		});
	}
}
